import { defineStore } from 'pinia';

const STORAGE_KEY = 'pinia-auth';

function readJson(key) {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
}

function getInitialState() {
  const piniaAuth = readJson(STORAGE_KEY);
  if (piniaAuth) {
    return {
      userData: piniaAuth.userData || null,
      token: piniaAuth.token || '',
      isLoading: false,
      error: null,
      // Menu and Permissions
      userAllPermissions: piniaAuth.userAllPermissions || [],
      userRoutes: piniaAuth.userRoutes || [],
      userNavigations: piniaAuth.userNavigations || [],
      dataCheck: piniaAuth.dataCheck || 0,
    };
  }

  const vuexState = readJson('vuex');
  return {
    userData: vuexState && vuexState.login ? vuexState.login.userData || null : null,
    token: vuexState && vuexState.login ? vuexState.login.token || '' : '',
    isLoading: false,
    error: null,
    userAllPermissions: [],
    userRoutes: [],
    userNavigations: [],
    dataCheck: 0,
  };
}

export const useAuthStore = defineStore('auth', {
  state: () => getInitialState(),

  getters: {
    isAuthenticated: (state) => !!state.token,
    authHeader: (state) => state.token ? `Bearer ${state.token}` : '',
    isAdmin: (state) => state.userData?.user?.roles?.includes(1),
    isSuperAdmin: (state) => state.userData?.user?.roles?.includes(0),
    isPOSUser: (state) => state.userData?.user?.roles?.some(role => [3, 7].includes(role)),
    userRoles: (state) => state.userData?.user?.roles || [],
    userId: (state) => state.userData?.user?.id,
    userName: (state) => state.userData?.user?.name,
  },

  actions: {
    persist() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        userData: this.userData,
        token: this.token,
        userAllPermissions: this.userAllPermissions,
        userRoutes: this.userRoutes,
        userNavigations: this.userNavigations,
        dataCheck: this.dataCheck,
      }));
    },

    setUser(userData) {
      this.userData = userData;
      this.persist();
    },

    setToken(token) {
      this.token = token;
      this.persist();
    },

    setAuth(userData, token) {
      this.userData = userData;
      this.token = token;
      this.error = null;
      this.persist();
    },

    setLoading(loading) {
      this.isLoading = loading;
    },

    setError(error) {
      this.error = error;
    },

    setMenuAndPermissions(permissions) {
      this.userAllPermissions = permissions.module_based_navigation || [];
      this.userRoutes = permissions.user_routes || [];
      this.userNavigations = permissions.user_navigations || [];
      this.dataCheck = permissions.data_check || 0;
      this.persist();
    },

    setUserAllPermissions(permissions) {
      this.userAllPermissions = permissions;
      this.persist();
    },

    setUserRoutes(routes) {
      this.userRoutes = routes;
      this.persist();
    },

    setUserNavigations(navigations) {
      this.userNavigations = navigations;
      this.persist();
    },

    setDataCheck(dataCheck) {
      this.dataCheck = dataCheck;
      this.persist();
    },

    async login(credentials) {
      this.isLoading = true;
      this.error = null;
      
      try {
        // This action will be called from the component
        // where the API call is made
      } catch (error) {
        this.error = error.response?.data?.message || 'Login failed';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    logout() {
      this.userData = null;
      this.token = '';
      this.error = null;
      this.userAllPermissions = [];
      this.userRoutes = [];
      this.userNavigations = [];
      this.dataCheck = 0;
      localStorage.removeItem(STORAGE_KEY);
    },

    clearError() {
      this.error = null;
    },

    // Restore auth from storage (useful on app initialization)
    restoreAuth() {
      const stored = readJson(STORAGE_KEY);
      if (stored && stored.token) {
        this.userData = stored.userData;
        this.token = stored.token;
        this.userAllPermissions = stored.userAllPermissions || [];
        this.userRoutes = stored.userRoutes || [];
        this.userNavigations = stored.userNavigations || [];
        this.dataCheck = stored.dataCheck || 0;
      }
    },
  },
});
